import { useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ProductsSelect, ProductSetsSelect } from 'modules/product';
import type { ProductSelectOption, ProductSetSelectOption } from 'modules/product';
import InputQuantity from 'components/others/InputQuantity';
import spinnerTitle from 'components/others/spinnerTitle';
import SolarIcon from 'components/icons/SolarIcon';

interface CreateItemsAddBoxProps {
    vendorId: number | null;
    sourceType: 'product' | 'set';
    setSourceType: (type: 'product' | 'set') => void;
    selectedProduct: ProductSelectOption | null;
    setSelectedProduct: (opt: ProductSelectOption | null) => void;
    selectedSet: ProductSetSelectOption | null;
    setSelectedSet: (opt: ProductSetSelectOption | null) => void;
    draftCount: number;
    setDraftCount: (updater: number | ((prev: number) => number)) => void;
    isAdding: boolean;
    busy: boolean;
    onAdd: () => void;
}

const CreateItemsAddBox = ({
    vendorId,
    sourceType,
    setSourceType,
    selectedProduct,
    setSelectedProduct,
    selectedSet,
    setSelectedSet,
    draftCount,
    setDraftCount,
    isAdding,
    busy,
    onAdd,
}: CreateItemsAddBoxProps) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const quantityRef = useRef<HTMLInputElement>(null);

    const hasSelection =
        sourceType === 'product' ? !!selectedProduct?.value : !!selectedSet?.value;
    const selectionId =
        sourceType === 'product' ? selectedProduct?.value : selectedSet?.value;

    useEffect(() => {
        if (!selectionId) return;
        quantityRef.current?.focus();
    }, [selectionId]);

    const setDraftCountDown = () => {
        setDraftCount((prev) => Math.max(1, Number(prev || 1) - 1));
    };
    const setDraftCountUp = () => {
        setDraftCount((prev) => Number(prev || 0) + 1);
    };
    const setDraftCountFromInput = (value: string) => {
        const next = Number(value);
        setDraftCount(Number.isFinite(next) ? next : 1);
    };

    return (
        <div className="mx-3 mb-3 p-3 border rounded off-coming-add-box">
            <div className="btn-group w-100 mb-3">
                <Button
                    size="sm"
                    variant={sourceType === 'product' ? 'primary' : 'outline-primary'}
                    onClick={() => setSourceType('product')}
                    disabled={busy}
                >
                    <SolarIcon name="box-seam" className="me-1" />
                    {t('supplierRequests.create.modeProduct')}
                </Button>
                <Button
                    size="sm"
                    variant={sourceType === 'set' ? 'primary' : 'outline-primary'}
                    onClick={() => setSourceType('set')}
                    disabled={busy}
                >
                    <SolarIcon name="collection" className="me-1" />
                    {t('supplierRequests.create.modeSet')}
                </Button>
            </div>

            {sourceType === 'product' ? (
                <ProductsSelect
                    forceIdVendor={vendorId}
                    disabled={busy}
                    value={selectedProduct?.value ?? null}
                    title={t('supplierRequests.create.labelProduct')}
                    required={false}
                    onChange={(opt) => setSelectedProduct(opt)}
                />
            ) : (
                <ProductSetsSelect
                    disabled={busy}
                    value={selectedSet?.value || null}
                    title={t('supplierRequests.create.labelSet')}
                    required={false}
                    onChange={(opt) => setSelectedSet(opt)}
                />
            )}

            {hasSelection ? (
                <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch align-items-sm-end mt-2">
                    <InputQuantity
                        ref={quantityRef}
                        label={t('supplierRequests.create.labelCount')}
                        value={Number(draftCount) || 1}
                        onDown={setDraftCountDown}
                        onUp={setDraftCountUp}
                        onChange={setDraftCountFromInput}
                        onEnter={onAdd}
                    />
                    <Button
                        size="sm"
                        variant="success"
                        disabled={busy}
                        onClick={() => onAdd()}
                    >
                        {spinnerTitle(
                            t('supplierRequests.create.btnAdd'),
                            isAdding,
                            t('supplierRequests.create.btnAdd'),
                        )}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

export default CreateItemsAddBox;
